import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Package, Mail, Lock, User, Building2, ArrowRight, KeyRound, Eye, EyeOff } from "lucide-react";

export default function Register() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", confirmPassword: "", organization_name: "", registration_code: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirmPassword) {
      setError("Le password non corrispondono");
      return;
    }
    if (form.password.length < 8) {
      setError("La password deve avere almeno 8 caratteri");
      return;
    }
    if (!/[A-Z]/.test(form.password) || !/[a-z]/.test(form.password) || !/[0-9]/.test(form.password) || !/[!@#$%^&*()_+\-=[\]{}|;:,.<>?/]/.test(form.password)) {
      setError("La password deve contenere maiuscola, minuscola, numero e carattere speciale");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", {
        name: form.name,
        email: form.email,
        password: form.password,
        organization_name: form.organization_name,
        registration_code: form.registration_code,
      });
      setUser(data);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left: Form */}
      <div className="flex-1 flex flex-col justify-center px-8 sm:px-16 lg:px-24 max-w-xl mx-auto lg:mx-0">
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 bg-[#002FA7] rounded-sm flex items-center justify-center">
              <Package className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-semibold tracking-tight font-['Outfit']">Traslochi</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter font-['Outfit'] mb-2">
            Crea il tuo account
          </h1>
          <p className="text-zinc-500 text-sm">
            Registrati come proprietario e inizia a gestire i tuoi traslochi
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div data-testid="register-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-sm px-4 py-3">
              {error}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="regcode" className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Codice Registrazione</Label>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input id="regcode" data-testid="register-code-input" type="password" value={form.registration_code} onChange={set("registration_code")} placeholder="Inserisci il codice di registrazione" className="pl-10 rounded-sm" required />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="org" className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Nome Azienda</Label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input id="org" data-testid="register-org-input" value={form.organization_name} onChange={set("organization_name")} placeholder="Es: Traslochi Rossi SRL" className="pl-10 rounded-sm" required />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="name" className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Nome e Cognome</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input id="name" data-testid="register-name-input" value={form.name} onChange={set("name")} placeholder="Mario Rossi" className="pl-10 rounded-sm" required />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input id="email" data-testid="register-email-input" type="email" value={form.email} onChange={set("email")} placeholder="nome@azienda.it" className="pl-10 rounded-sm" required />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input id="password" data-testid="register-password-input" type={showPassword ? "text" : "password"} value={form.password} onChange={set("password")} placeholder="Min. 8 caratteri" className="pl-10 pr-10 rounded-sm" required minLength={8} />
                <button type="button" data-testid="toggle-register-password" onClick={() => setShowPassword((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors" tabIndex={-1}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm" className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Conferma Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input id="confirm" data-testid="register-confirm-password-input" type={showConfirm ? "text" : "password"} value={form.confirmPassword} onChange={set("confirmPassword")} placeholder="Ripeti password" className="pl-10 pr-10 rounded-sm" required minLength={8} />
                <button type="button" data-testid="toggle-register-confirm" onClick={() => setShowConfirm((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors" tabIndex={-1}>
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          <Button
            type="submit"
            data-testid="register-submit-button"
            disabled={loading}
            className="w-full bg-[#002FA7] hover:bg-[#002482] rounded-sm h-11 font-medium mt-2"
          >
            {loading ? "Registrazione in corso..." : "Registrati"}
            {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-zinc-500">
          Hai già un account?{" "}
          <Link to="/login" className="text-[#002FA7] font-medium hover:underline" data-testid="go-to-login-link">
            Accedi
          </Link>
        </p>
      </div>

      {/* Right: Hero */}
      <div className="hidden lg:block lg:flex-1 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-[#002FA7] to-[#001B61]">
          <div className="absolute inset-0 flex items-end p-16">
            <div>
              <h2 className="text-white text-3xl font-bold font-['Outfit'] tracking-tight mb-3">
                Inizia in pochi secondi
              </h2>
              <p className="text-white/70 text-sm max-w-md">
                Crea la tua organizzazione, aggiungi i tuoi operatori e inizia subito a creare preventivi, inventari e fatture proforma.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
